import { FC, useEffect, useState } from 'react';
import {
  Checkbox,
  InlineNotification,
  Modal,
  RadioButton,
  RadioButtonGroup,
} from '@carbon/react';
import type {
  EntityAddress,
  MoveAddressDestination,
  StructuredPartyListItem,
} from './types';
import { cardAddresses } from './utils';
import style from './move-address-modal.module.scss';

interface Props {
  open: boolean;
  party: StructuredPartyListItem | null;
  allParties: StructuredPartyListItem[];
  onClose: () => void;
  onSubmit: (
    addressId: number,
    destination: MoveAddressDestination
  ) => Promise<void>;
}

const addressLabel = (
  addr: EntityAddress & { primary?: boolean },
  index: number
): string => {
  const text = addr.fullText || addr.asFoundAddress || `Address ${index + 1}`;
  return addr.primary ? `Primary: ${text}` : text;
};

interface PartyOption {
  id: number;
  label: string;
  disabled: boolean;
}

const buildPartyOptions = (
  source: StructuredPartyListItem,
  all: StructuredPartyListItem[]
): PartyOption[] =>
  all
    .filter(
      (p) =>
        !(p.kind === source.kind && p.id === source.id) &&
        p.status !== 'removed'
    )
    .map((p) => {
      const number = p.partyNumber ? `Party ${p.partyNumber}` : 'Party';
      const name = p.name || 'Unnamed';
      const role = p.role ? ` · ${p.role}` : '';
      const lockTag = p.status === 'complete' ? ' (locked — complete)' : '';
      return {
        id: p.id,
        label: `${number} · ${name}${role}${lockTag}`,
        disabled: p.status === 'complete',
      };
    });

export const MoveAddressModal: FC<Props> = ({
  open,
  party,
  allParties,
  onClose,
  onSubmit,
}) => {
  const addresses = cardAddresses(party?.stagedEntity);
  const [selectedAddrId, setSelectedAddrId] = useState<number | null>(null);
  const [destinationType, setDestinationType] = useState<'document' | 'party'>(
    'document'
  );
  const [targetPartyIds, setTargetPartyIds] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const partyOptions = party ? buildPartyOptions(party, allParties) : [];
  const hasAnySelectableTarget = partyOptions.some((p) => !p.disabled);

  useEffect(() => {
    if (open && party) {
      setSelectedAddrId(addresses[0]?.id ?? null);
      setDestinationType('document');
      setTargetPartyIds(new Set());
      setError(null);
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, party?.id, party?.kind]);

  if (!party) return null;

  const canSubmit =
    selectedAddrId !== null &&
    !submitting &&
    (destinationType === 'document' ||
      (destinationType === 'party' && targetPartyIds.size > 0));

  const handleSubmit = async () => {
    if (!canSubmit || selectedAddrId === null) return;
    setError(null);
    setSubmitting(true);
    try {
      const destination: MoveAddressDestination =
        destinationType === 'party'
          ? { type: 'party', targetPartyIds: Array.from(targetPartyIds) }
          : { type: 'document' };
      await onSubmit(selectedAddrId, destination);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not move address.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTarget = (id: number, checked: boolean) =>
    setTargetPartyIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  return (
    <Modal
      open={open}
      modalHeading="Move Address"
      primaryButtonText={submitting ? 'Moving…' : 'Confirm'}
      secondaryButtonText="Cancel"
      size="sm"
      primaryButtonDisabled={!canSubmit}
      onRequestClose={onClose}
      onSecondarySubmit={onClose}
      onRequestSubmit={handleSubmit}
    >
      {addresses.length > 1 && (
        <>
          <p className={style.sectionHeading}>
            Which address do you want to move?
          </p>
          <RadioButtonGroup
            name="move-address-source"
            orientation="vertical"
            valueSelected={
              selectedAddrId !== null ? String(selectedAddrId) : undefined
            }
            onChange={(v) => setSelectedAddrId(Number(v))}
          >
            {addresses.map((addr, i) => (
              <RadioButton
                key={addr.id}
                id={`move-addr-source-${addr.id}`}
                value={String(addr.id)}
                labelText={addressLabel(addr, i)}
              />
            ))}
          </RadioButtonGroup>
          <div className={style.sectionSpacer} />
        </>
      )}

      <p className={style.sectionHeading}>Destination</p>
      <RadioButtonGroup
        name="move-address-destination"
        orientation="vertical"
        valueSelected={destinationType}
        onChange={(v) => setDestinationType(v as 'document' | 'party')}
      >
        <RadioButton
          id="move-dest-document"
          value="document"
          labelText="Non-Party Addresses"
        />
        <RadioButton
          id="move-dest-party"
          value="party"
          labelText="Other parties (select one or more)"
          disabled={partyOptions.length === 0 || !hasAnySelectableTarget}
        />
      </RadioButtonGroup>

      {destinationType === 'party' && (
        <div className={style.targetList}>
          {partyOptions.length === 0 ? (
            <p className={style.emptyHint}>No other parties available.</p>
          ) : (
            partyOptions.map((opt) => (
              <Checkbox
                key={opt.id}
                id={`move-target-${opt.id}`}
                labelText={opt.label}
                disabled={opt.disabled}
                checked={targetPartyIds.has(opt.id)}
                onChange={(_e, { checked }) => toggleTarget(opt.id, !!checked)}
              />
            ))
          )}
        </div>
      )}

      <p className={style.helpText}>
        {destinationType === 'document'
          ? `The selected address will be detached from "${
              party.name || 'this party'
            }" and moved to Non-Party Addresses for review.`
          : `The selected address will be detached from "${
              party.name || 'this party'
            }" and added to each of the selected parties (one copy per party).`}
      </p>

      {error && (
        <div className={style.errorBox}>
          <InlineNotification
            kind="error"
            lowContrast
            title="Error"
            subtitle={error}
            hideCloseButton
          />
        </div>
      )}
    </Modal>
  );
};
